import { locationDisplayAddress } from '../../../services/locationAddress'
import React, { useEffect, useMemo, useState } from 'react'
import {
  archiveOrgUnit,
  createOrganizationUnit,
  createScopedRoleAssignment,
  listOrganizationRoles,
  listOrganizationUnits,
  restoreOrgUnit,
  updateOrganizationUnit,
} from '../../../services/access'
import type {
  AccessibleLocation,
  Organization,
  OrganizationUnit,
  OrganizationUnitPayload,
  OrganizationUnitType,
  TenantRoleSummary,
} from '../../../types/access'
import { formatOrgType } from './adminPanelUtils'
import { ConfirmDialog, DetailDrawer, EmptyState, EntityActionMenu, ErrorBanner, FormModal, LoadingState } from './AdminUiPrimitives'
import { useI18n } from '../../../i18n/I18nContext'

type Translate = (text: string, vars?: Record<string, string | number>) => string

interface OrgTreePanelProps {
  organizations: Organization[]
  contextUnits: OrganizationUnit[]
  accessibleLocations: AccessibleLocation[]
  selectedOrgId: string
  canManageOrganizations: boolean
  onSelectOrg: (orgId: string) => void
}

type OuModal = 'create' | 'edit' | 'move' | 'assign-location' | 'assign-role' | 'archive' | null

const ouTypes: OrganizationUnitType[] = ['root', 'region', 'company', 'location', 'department', 'team', 'custom']
const childrenFor = (units: OrganizationUnit[], parentId: string | null) => units.filter((unit) => (unit.parent_ou_id || null) === parentId)
const roleLabel = (role: TenantRoleSummary, t: Translate) => {
  const label = role.label || role.role_key || role.id
  return role.permission_count ? t('{label} ({count} permissions)', { label, count: role.permission_count }) : label
}
const locationLabel = (location: AccessibleLocation) => [location.name || location.label || location.location_key || location.id, locationDisplayAddress(location)].filter(Boolean).join(' - ')

const UnitTree: React.FC<{
  units: OrganizationUnit[]
  parentId?: string | null
  depth?: number
  canManageOrganizations: boolean
  t: Translate
  onView: (unit: OrganizationUnit) => void
  onAction: (unit: OrganizationUnit, modal: Exclude<OuModal, null>) => void
}> = ({ units, parentId = null, depth = 0, canManageOrganizations, t, onView, onAction }) => {
  const children = childrenFor(units, parentId)
  if (children.length === 0) return null

  return (
    <ul className="admin-tree-list">
      {children.map((unit) => (
        <li key={unit.id} style={{ marginLeft: depth ? '.8rem' : 0 }}>
          <div className="admin-tree-node">
            <button type="button" className="btn btn-ghost" onClick={() => onView(unit)}>
              <strong>{unit.name}</strong>
              {unit.path ? <span className="helper"> {unit.path}</span> : null}
            </button>
            <span className="status-pill">{t(unit.ou_type)}</span>
            <EntityActionMenu>
              <button type="button" className="btn btn-ghost" onClick={() => onView(unit)}>{t('View details')}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations} onClick={() => onAction(unit, 'edit')}>{t('Edit')}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations} onClick={() => onAction(unit, 'move')}>{t('Move')}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations} onClick={() => onAction(unit, 'assign-location')}>{t('Assign location')}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations} onClick={() => onAction(unit, 'assign-role')}>{t('Assign scoped role')}</button>
              <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations} onClick={() => onAction(unit, 'archive')}>{unit.status === 'archived' ? t('Restore') : t('Archive')}</button>
            </EntityActionMenu>
          </div>
          <UnitTree units={units} parentId={unit.id} depth={depth + 1} canManageOrganizations={canManageOrganizations} t={t} onView={onView} onAction={onAction} />
        </li>
      ))}
    </ul>
  )
}

export const OrgTreePanel: React.FC<OrgTreePanelProps> = ({
  organizations,
  contextUnits,
  accessibleLocations,
  selectedOrgId,
  canManageOrganizations,
  onSelectOrg,
}) => {
  const { t, formatDate } = useI18n()
  const [units, setUnits] = useState<OrganizationUnit[]>([])
  const [roles, setRoles] = useState<TenantRoleSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [selectedUnit, setSelectedUnit] = useState<OrganizationUnit | null>(null)
  const [activeModal, setActiveModal] = useState<OuModal>(null)
  const [archiveTarget, setArchiveTarget] = useState<OrganizationUnit | null>(null)
  const [form, setForm] = useState<OrganizationUnitPayload>({
    name: '',
    ouType: 'department',
    parentOuId: null,
    linkedCompanyOrgId: null,
    linkedLocationId: null,
    path: '',
  })
  const [roleForm, setRoleForm] = useState({ userId: '', roleId: '', inheritsDown: true })

  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)
  const companyOrganizations = useMemo(() => organizations.filter((org) => org.org_type === 'company'), [organizations])
  const parentOptions = units.filter((unit) => unit.id !== selectedUnit?.id)
  const selectedLocation = selectedUnit?.linked_location_id ? accessibleLocations.find((location) => location.id === selectedUnit.linked_location_id) : null
  const selectedCompany = selectedUnit?.linked_company_org_id ? organizations.find((org) => org.id === selectedUnit.linked_company_org_id) : null

  useEffect(() => {
    if (!selectedOrgId) return
    const fallbackUnits = contextUnits.filter((unit) => unit.org_id === selectedOrgId)
    setUnits(fallbackUnits)
    let mounted = true
    setLoading(true)
    setError('')
    Promise.all([
      listOrganizationUnits(selectedOrgId),
      listOrganizationRoles(selectedOrgId).catch(() => [] as TenantRoleSummary[]),
    ])
      .then(([rows, roleRows]) => {
        if (!mounted) return
        setUnits(rows)
        setRoles(roleRows)
        setSelectedUnit((current) => (current && rows.some((unit) => unit.id === current.id) ? rows.find((unit) => unit.id === current.id) || current : null))
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load organization units.')))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [contextUnits, selectedOrgId])

  const refresh = async () => {
    if (!selectedOrgId) return
    const rows = await listOrganizationUnits(selectedOrgId)
    setUnits(rows)
    setSelectedUnit((current) => (current ? rows.find((unit) => unit.id === current.id) || null : null))
  }

  const resetForm = () => {
    setForm({ name: '', ouType: 'department', parentOuId: null, linkedCompanyOrgId: null, linkedLocationId: null, path: '' })
  }

  const openModal = (unit: OrganizationUnit | null, modal: Exclude<OuModal, null>) => {
    if (modal === 'archive') {
      setArchiveTarget(unit)
      setMessage('')
      setError('')
      return
    }
    setSelectedUnit(unit)
    setMessage('')
    setError('')
    if (unit) {
      setForm({
        name: unit.name,
        ouType: unit.ou_type as OrganizationUnitType,
        parentOuId: unit.parent_ou_id || null,
        linkedCompanyOrgId: unit.linked_company_org_id || null,
        linkedLocationId: unit.linked_location_id || null,
        path: unit.path || '',
      })
    } else {
      resetForm()
    }
    setRoleForm({ userId: '', roleId: '', inheritsDown: true })
    setActiveModal(modal)
  }

  const payloadFromForm = () => ({
    ...form,
    name: form.name.trim(),
    parentOuId: form.parentOuId || null,
    linkedCompanyOrgId: form.linkedCompanyOrgId || null,
    linkedLocationId: form.linkedLocationId || null,
    path: form.path?.trim() || undefined,
  })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageOrganizations || !selectedOrgId) return
    if (!form.name.trim()) {
      setError(t('Organization unit name is required.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (activeModal === 'create') {
        await createOrganizationUnit(selectedOrgId, payloadFromForm())
        setMessage(t('Organization unit created.'))
      } else if (selectedUnit) {
        await updateOrganizationUnit(selectedUnit.id, payloadFromForm())
        setMessage(activeModal === 'move' ? t('Organization unit moved.') : activeModal === 'assign-location' ? t('Location assigned.') : t('Organization unit updated.'))
      }
      await refresh()
      setActiveModal(null)
      resetForm()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to save organization unit.'))
    } finally {
      setSaving(false)
    }
  }

  const handleAssignRole = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedUnit || !canManageOrganizations) return
    if (!roleForm.userId.trim() || !roleForm.roleId.trim()) {
      setError(t('Choose a user and role before assigning OU access.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await createScopedRoleAssignment({
        userId: roleForm.userId.trim(),
        roleId: roleForm.roleId,
        scopeType: 'organization_unit',
        scopeId: selectedUnit.id,
        inheritsDown: roleForm.inheritsDown,
      })
      setActiveModal(null)
      setRoleForm({ userId: '', roleId: '', inheritsDown: true })
      setMessage(t('Scoped OU role assigned.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to assign scoped OU role.'))
    } finally {
      setSaving(false)
    }
  }

  const handleArchiveRestore = async () => {
    if (!archiveTarget || !canManageOrganizations) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (archiveTarget.status === 'archived' || archiveTarget.archived_at) {
        await restoreOrgUnit(archiveTarget.id)
        setMessage(t('Organization unit restored.'))
      } else {
        await archiveOrgUnit(archiveTarget.id)
        setMessage(t('Organization unit archived.'))
      }
      await refresh()
      setArchiveTarget(null)
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update organization unit archive status.'))
      setArchiveTarget(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stack" data-testid="admin-org-tree-panel">
      <div className="admin-panel-toolbar">
        <div className="form-row admin-select-row">
          <label htmlFor="org-tree-org">{t('Organization')}</label>
          <select id="org-tree-org" className="input" value={selectedOrgId} onChange={(event) => onSelectOrg(event.target.value)}>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name} ({t(formatOrgType(org.org_type))})</option>)}
          </select>
        </div>
        <button type="button" className="btn btn-primary" disabled={!canManageOrganizations || !selectedOrgId} onClick={() => openModal(null, 'create')}>{t('Create OU')}</button>
      </div>
      <p className="helper">{t('OU access inherits down through descendants in v1 when role assignments are marked to inherit. Location selectors are limited to the active access context.')}</p>

      {message ? <p className="helper">{message}</p> : null}
      <ErrorBanner message={error} />

      <article className="panel workspace-panel" style={{ background: 'var(--surface-muted)' }}>
        <div className="admin-panel-toolbar">
          <h3 style={{ marginTop: 0 }}>{selectedOrg?.name || t('Organization tree')}</h3>
          {!canManageOrganizations ? <p className="helper">{t('OU changes require tenant RBAC management permission.')}</p> : null}
        </div>
        {loading ? <LoadingState label={t('Loading organization units...')} /> : null}
        {units.length ? (
          <UnitTree units={units} canManageOrganizations={canManageOrganizations} t={t} onView={setSelectedUnit} onAction={openModal} />
        ) : (
          <EmptyState title={t('No organization units visible')} description={t('Create an OU to start organizing company, department, team, or location scopes.')} />
        )}
      </article>

      <DetailDrawer
        title={selectedUnit?.name || t('OU details')}
        subtitle={selectedUnit ? t('{type} scope', { type: t(selectedUnit.ou_type) }) : undefined}
        isOpen={Boolean(selectedUnit)}
        onClose={() => setSelectedUnit(null)}
        actions={(
          <>
            <button type="button" className="btn btn-primary" disabled={!selectedUnit || !canManageOrganizations} onClick={() => openModal(selectedUnit, 'edit')}>{t('Edit')}</button>
            <button type="button" className="btn btn-secondary" disabled={!selectedUnit || !canManageOrganizations} onClick={() => openModal(selectedUnit, 'move')}>{t('Move')}</button>
            <button type="button" className="btn btn-ghost" disabled={!selectedUnit || !canManageOrganizations} onClick={() => selectedUnit && setArchiveTarget(selectedUnit)}>{selectedUnit?.status === 'archived' ? t('Restore') : t('Archive')}</button>
          </>
        )}
      >
        {selectedUnit ? (
          <div className="stack">
            <div className="admin-detail-grid">
              <span>{t('Organization')}</span><strong>{selectedOrg?.name || '-'}</strong>
              <span>{t('Path')}</span><strong>{selectedUnit.path || '-'}</strong>
              <span>{t('Status')}</span><strong>{t(selectedUnit.status || 'active')}</strong>
              <span>{t('Parent OU')}</span><strong>{units.find((unit) => unit.id === selectedUnit.parent_ou_id)?.name || t('None')}</strong>
              <span>{t('Linked company')}</span><strong>{selectedCompany?.name || '-'}</strong>
              <span>{t('Linked location')}</span><strong>{selectedLocation ? locationLabel(selectedLocation) : '-'}</strong>
              <span>{t('Created')}</span><strong>{formatDate(selectedUnit.created_at)}</strong>
              <span>{t('Updated')}</span><strong>{formatDate(selectedUnit.updated_at)}</strong>
              <span>{t('Archived')}</span><strong>{formatDate(selectedUnit.archived_at)}</strong>
              <span>{t('Archive reason')}</span><strong>{selectedUnit.archive_reason || '-'}</strong>
            </div>
            <div className="inline-group">
              <button type="button" className="btn btn-secondary" disabled={!canManageOrganizations} onClick={() => openModal(selectedUnit, 'assign-location')}>{t('Assign location')}</button>
              <button type="button" className="btn btn-secondary" disabled={!canManageOrganizations} onClick={() => openModal(selectedUnit, 'assign-role')}>{t('Assign scoped role')}</button>
            </div>
          </div>
        ) : null}
      </DetailDrawer>

      <FormModal
        title={activeModal === 'create' ? t('Create OU') : activeModal === 'move' ? t('Move OU') : activeModal === 'assign-location' ? t('Assign location') : t('Edit OU')}
        description={selectedOrg ? t('Tenant-scoped changes for {org}.', { org: selectedOrg.name }) : undefined}
        isOpen={activeModal === 'create' || activeModal === 'edit' || activeModal === 'move' || activeModal === 'assign-location'}
        onClose={() => setActiveModal(null)}
        size={activeModal === 'move' || activeModal === 'assign-location' ? 'md' : 'lg'}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setActiveModal(null)}>{t('Cancel')}</button>
            <button type="submit" form="ou-form" className="btn btn-primary" disabled={!canManageOrganizations || saving}>{saving ? t('Saving...') : t('Save OU')}</button>
          </>
        )}
      >
        <form id="ou-form" className="stack" onSubmit={handleSubmit}>
          {activeModal === 'move' ? (
            <div className="form-row">
              <label htmlFor="ou-parent">{t('Parent OU')}</label>
              <select id="ou-parent" className="input" value={form.parentOuId || ''} onChange={(event) => setForm({ ...form, parentOuId: event.target.value || null })} disabled={!canManageOrganizations}>
                <option value="">{t('None')}</option>
                {parentOptions.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
              </select>
            </div>
          ) : activeModal === 'assign-location' ? (
            <div className="form-row">
              <label htmlFor="ou-location">{t('Linked location')}</label>
              <select id="ou-location" className="input" value={form.linkedLocationId || ''} onChange={(event) => setForm({ ...form, linkedLocationId: event.target.value || null })} disabled={!canManageOrganizations || accessibleLocations.length === 0}>
                <option value="">{accessibleLocations.length ? t('None') : t('No accessible locations')}</option>
                {accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
              </select>
            </div>
          ) : (
            <div className="admin-form-grid">
              <div className="form-row">
                <label htmlFor="ou-name">{t('Name')}</label>
                <input id="ou-name" className="input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} disabled={!canManageOrganizations} />
              </div>
              <div className="form-row">
                <label htmlFor="ou-type">{t('OU type')}</label>
                <select id="ou-type" className="input" value={form.ouType} onChange={(event) => setForm({ ...form, ouType: event.target.value as OrganizationUnitType })} disabled={!canManageOrganizations}>
                  {ouTypes.map((type) => <option key={type} value={type}>{t(type)}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="ou-parent-edit">{t('Parent OU')}</label>
                <select id="ou-parent-edit" className="input" value={form.parentOuId || ''} onChange={(event) => setForm({ ...form, parentOuId: event.target.value || null })} disabled={!canManageOrganizations}>
                  <option value="">{t('None')}</option>
                  {parentOptions.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="ou-company">{t('Linked company')}</label>
                <select id="ou-company" className="input" value={form.linkedCompanyOrgId || ''} onChange={(event) => setForm({ ...form, linkedCompanyOrgId: event.target.value || null })} disabled={!canManageOrganizations}>
                  <option value="">{t('None')}</option>
                  {companyOrganizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="ou-location-edit">{t('Linked location')}</label>
                <select id="ou-location-edit" className="input" value={form.linkedLocationId || ''} onChange={(event) => setForm({ ...form, linkedLocationId: event.target.value || null })} disabled={!canManageOrganizations || accessibleLocations.length === 0}>
                  <option value="">{accessibleLocations.length ? t('None') : t('No accessible locations')}</option>
                  {accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="ou-path">{t('Path')}</label>
                <input id="ou-path" className="input" value={form.path || ''} onChange={(event) => setForm({ ...form, path: event.target.value })} disabled={!canManageOrganizations} placeholder={selectedOrg ? `/${selectedOrg.slug || selectedOrg.name.toLowerCase()}` : '/'} />
              </div>
            </div>
          )}
        </form>
      </FormModal>

      <FormModal
        title={t('Assign scoped OU role')}
        description={selectedUnit ? t('Role assignment will target {unit} and may inherit down.', { unit: selectedUnit.name }) : undefined}
        isOpen={activeModal === 'assign-role' && Boolean(selectedUnit)}
        onClose={() => setActiveModal(null)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setActiveModal(null)}>{t('Cancel')}</button>
            <button type="submit" form="ou-role-form" className="btn btn-primary" disabled={!canManageOrganizations || saving}>{saving ? t('Assigning...') : t('Assign role')}</button>
          </>
        )}
      >
        <form id="ou-role-form" className="admin-form-grid" onSubmit={handleAssignRole}>
          <div className="form-row">
            <label>{t('User UUID')}</label>
            <input className="input" value={roleForm.userId} onChange={(event) => setRoleForm({ ...roleForm, userId: event.target.value })} />
          </div>
          <div className="form-row">
            <label>{t('Role')}</label>
            <select className="input" value={roleForm.roleId} onChange={(event) => setRoleForm({ ...roleForm, roleId: event.target.value })}>
              <option value="">{t('Select role')}</option>
              {roles.map((role) => <option key={role.id} value={role.id}>{roleLabel(role, t)}</option>)}
            </select>
          </div>
          <label className="inline-group" style={{ alignItems: 'center' }}><input type="checkbox" checked={roleForm.inheritsDown} onChange={(event) => setRoleForm({ ...roleForm, inheritsDown: event.target.checked })} /> <span>{t('Inherits down')}</span></label>
        </form>
      </FormModal>

      <ConfirmDialog
        title={archiveTarget?.status === 'archived' ? t('Restore OU?') : t('Archive OU?')}
        message={archiveTarget ? archiveTarget.status === 'archived'
          ? t('{name} will be restored. Parent OUs must already be active.', { name: archiveTarget.name })
          : t('{name} will be soft archived. OUs with active children or assigned active locations are rejected by the backend. Hard delete remains unsupported.', { name: archiveTarget.name }) : ''}
        isOpen={Boolean(archiveTarget)}
        confirmLabel={archiveTarget?.status === 'archived' ? t('Restore') : t('Archive')}
        destructive
        disabled={saving}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={handleArchiveRestore}
      />
    </div>
  )
}
